import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { getModernScrollbar, layoutGap } from '../../../theme/theme';

export interface VirtualizedListProps<T> {
  items: T[];
  itemHeight: number;
  getKey: (item: T, index: number) => React.Key;
  renderItem: (item: T, index: number) => React.ReactNode;
  overscan?: number;
  sx?: SxProps<Theme>;
  role?: string;
  ariaLabel?: string;
}

const DEFAULT_OVERSCAN_ROWS = 5;

export default function VirtualizedList<T>({
  items,
  itemHeight,
  getKey,
  renderItem,
  overscan = DEFAULT_OVERSCAN_ROWS,
  sx,
  role,
  ariaLabel,
}: VirtualizedListProps<T>) {
  const theme = useTheme();
  const scrollbarStyles = useMemo(() => getModernScrollbar(theme), [theme]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  const measureViewport = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    setViewportHeight(containerRef.current.clientHeight);
  }, []);

  useEffect(() => {
    measureViewport();
    window.addEventListener('resize', measureViewport);
    return () => window.removeEventListener('resize', measureViewport);
  }, [measureViewport]);

  useEffect(() => {
    const maxScrollTop = Math.max(0, items.length * itemHeight - viewportHeight);
    if (scrollTop <= maxScrollTop) {
      return;
    }

    setScrollTop(maxScrollTop);
    if (containerRef.current) {
      containerRef.current.scrollTop = maxScrollTop;
    }
  }, [itemHeight, items.length, scrollTop, viewportHeight]);

  const visibleRange = useMemo(() => {
    if (items.length === 0) {
      return { start: 0, end: 0 };
    }

    const safeViewportHeight = viewportHeight || itemHeight * 4;
    const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const end = Math.min(
      items.length,
      Math.ceil((scrollTop + safeViewportHeight) / itemHeight) + overscan
    );

    return { start, end };
  }, [itemHeight, items.length, overscan, scrollTop, viewportHeight]);

  return (
    <Box
      ref={containerRef}
      role={role}
      aria-label={ariaLabel}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      sx={[
        { flex: 1, overflowY: 'auto', minHeight: 0, ...scrollbarStyles },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box sx={{ position: 'relative', height: items.length * itemHeight }}>
        {items.slice(visibleRange.start, visibleRange.end).map((item, offset) => {
          const index = visibleRange.start + offset;
          return (
            <Box
              key={getKey(item, index)}
              role={role ? 'listitem' : undefined}
              sx={{
                position: 'absolute',
                top: index * itemHeight,
                left: 0,
                right: 0,
                height: itemHeight,
                boxSizing: 'border-box',
                pb: layoutGap.snug,
              }}
            >
              {renderItem(item, index)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
